<template>
  <div class="parceiros-wrapper">
    
    <v-container>
      <div class="text-center pt-10 mb-16">
        <h2 class="text-overline red--text font-weight-bold mb-2 tracking-widest">
          CONEXÕES
        </h2>
        <h1 class="text-h4 text-md-h2 font-weight-black white--text text-uppercase mb-6">
          Nossos Parceiros
        </h1>
        <div class="mx-auto gradient-line"></div>
        <p class="grey--text text--lighten-1 mt-6 text-body-1" style="max-width: 800px; margin-left: auto; margin-right: auto;">
          Empresas e instituições que confiam no nosso trabalho e mergulham fundo com a gente.
        </p>
      </div>

      <div v-if="loading" class="text-center py-16">
        <v-progress-circular indeterminate color="#D32F2F" size="60"></v-progress-circular>
      </div>

      <v-row v-else justify="center">
        <v-col 
          v-for="(parceiro, index) in parceiros" 
          :key="parceiro.id"
          cols="12" sm="6" md="4" lg="3"
        >
          <v-hover v-slot="{ hover }">
            <v-card 
              color="#1a1a1a" 
              class="rounded-xl pa-6 h-100 d-flex flex-column align-center elevation-6 transition-swing"
              :class="{ 'on-hover': hover }"
              style="border: 1px solid rgba(255,255,255,0.05);"
            >
              <div 
                class="clickable-area text-center w-100" 
                @click="abrirLink(parceiro.link)"
                v-ripple
              >
                <v-avatar size="120" class="mb-6 grey lighten-2 elevation-4 pointer">
                  <v-img 
                    :src="parceiro.logo && parceiro.logo[0] ? parceiro.logo[0].url : ''" 
                    alt="Logo"
                  >
                      <template v-slot:placeholder>
                         <v-icon large color="grey">mdi-handshake</v-icon>
                      </template>
                  </v-img>
                </v-avatar>
                
                <h3 class="white--text text-h6 font-weight-bold mb-4 pointer hover-underline">
                  {{ parceiro.nome }}
                </h3>
              </div>

              <div class="text-content w-100 mb-4">
                <p 
                  class="grey--text text--lighten-1 text-caption mb-1 text-justify" 
                  style="white-space: pre-line; line-height: 1.6;"
                >
                  <span v-if="parceiro.expandido">
                    {{ parceiro.descricao }}
                  </span>
                  <span v-else>
                    {{ getResumo(parceiro.descricao) }}
                  </span>
                </p>

                <div v-if="parceiro.descricao && parceiro.descricao.length > 150" class="text-right">
                  <v-btn 
                    text 
                    x-small 
                    color="grey lighten-1" 
                    class="px-0"
                    @click.stop="toggleExpandir(index)"
                  >
                    {{ parceiro.expandido ? 'Ler menos' : 'Ver mais' }}
                    <v-icon right x-small>{{ parceiro.expandido ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </div>
              </div>
              
              <v-spacer></v-spacer>
              
              <v-btn 
                v-if="parceiro.link" 
                @click="abrirLink(parceiro.link)"
                outlined
                rounded
                color="red" 
                small 
                class="mt-2 px-6 font-weight-bold btn-visitar"
              >
                Visitar Site <v-icon right small>mdi-open-in-new</v-icon>
              </v-btn>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>

      <div v-if="!loading && parceiros.length === 0" class="text-center grey--text py-16">
        <v-icon size="60" color="grey darken-3" class="mb-4">mdi-account-search</v-icon>
        <p>Ainda não temos parceiros cadastrados.</p>
      </div>
    </v-container>
  </div>
</template>

<script>
import ParceirosService from '@/services/parceiros.service';

export default {
  name: "ParceirosPage",
  data() {
    return {
      loading: true,
      parceiros: []
    };
  },
  methods: {
    // Abre o link em nova aba
    abrirLink(link) {
      if (link) {
        window.open(link, '_blank');
      }
    },
    // Alterna entre mostrar tudo ou resumido
    toggleExpandir(index) {
      // Usamos $set para garantir que o Vue detecte a mudança na array
      const parceiro = this.parceiros[index];
      this.$set(this.parceiros, index, { ...parceiro, expandido: !parceiro.expandido });
    },
    // Corta o texto para o resumo
    getResumo(texto) {
      if (!texto) return '';
      if (texto.length <= 150) return texto;
      return texto.substring(0, 150) + '...';
    },
    async carregarParceiros() {
      this.loading = true;
      try {
        // response já contém o array de dados porque o service faz 'return response.data'
        const response = await ParceirosService.getAll();
        
        let lista = response || [];
        
        lista.sort((a, b) => {
          const ordemA = (a.ordem !== undefined && a.ordem !== null) ? Number(a.ordem) : 99;
          const ordemB = (b.ordem !== undefined && b.ordem !== null) ? Number(b.ordem) : 99;
          return ordemA - ordemB;
        });

        // Mapeando os dados do backend para as propriedades que o template Vue usa
        this.parceiros = lista.map(p => ({
          ...p,
          nome: p.titulo, // backend envia 'titulo', template pede 'nome'
          logo: p.fotos && p.fotos.length > 0 ? [{ url: p.fotos[0] }] : [], // backend envia array de strings em 'fotos', template pede logo[0].url
          expandido: false
        }));
      } catch (error) {
        console.error("Erro ao carregar parceiros:", error);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.carregarParceiros();
  }
};
</script>

<style scoped>
.parceiros-wrapper {
  background-color: #121212;
  font-family: 'Roboto', sans-serif;
  min-height: 100vh;
  padding-bottom: 80px;
}

.tracking-widest {
  letter-spacing: 3px !important;
}

.gradient-line {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #D32F2F 0%, #ff7961 100%);
  border-radius: 4px;
}

.transition-swing {
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.on-hover {
  transform: translateY(-5px);
  border-color: #D32F2F !important;
  box-shadow: 0 10px 30px rgba(211, 47, 47, 0.15) !important;
}

/* Cursores e interações */
.pointer {
  cursor: pointer;
}

.clickable-area:hover .hover-underline {
  text-decoration: underline;
  text-decoration-color: #D32F2F;
}

.w-100 {
  width: 100%;
}

.text-justify {
  text-align: justify;
}
</style>